import type { UseFormReturnType } from '@mantine/form';
import type { AutocompleteItem } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { reportEvent } from '~/domains/analytics';
import { createEnterKeyHandler } from '~/domains/auth/utils/formHelpers';
import useAddressLookup from '~/domains/user/hooks/useAddressLookup';
import { Countries } from '~/common/constants/countries.enum';

import { KYCDetailsMode } from '../types';
import type { KYCFormValues } from '../types';

import { useCountrySelectData } from './useCountrySelectData';
import { useKYCFormHelpers } from './useKYCFormHelpers';

function getHeaderStrings(
  mode: KYCDetailsMode,
  t: (key: string) => string
): { title: string; copy: string } {
  switch (mode) {
    case KYCDetailsMode.VERIFY:
      return {
        title: t('signUp.headers.kycDetails.verifyTitle'),
        copy: t('signUp.headers.kycDetails.verifyCopy'),
      };
    case KYCDetailsMode.EDIT:
      return {
        title: t('signUp.headers.kycDetails.editTitle'),
        copy: t('signUp.headers.kycDetails.copy'),
      };
    default:
      return {
        title: t('signUp.headers.kycDetails.manualTitle'),
        copy: t('signUp.headers.kycDetails.copy'),
      };
  }
}

interface UseKYCDetailsFormParams {
  form: UseFormReturnType<KYCFormValues>;
  onClick: () => void;
  mode: KYCDetailsMode;
}

export function useKYCDetailsForm({ form, onClick, mode }: UseKYCDetailsFormParams) {
  const { t } = useTranslation('auth');
  const countrySelectData = useCountrySelectData();
  const { formFieldChange, stateFieldChange } = useKYCFormHelpers(form);

  const isButtonDisabled = !form.isValid();

  const { dataForAutocomplete, getPlaceDetail } = useAddressLookup({
    query: form.values.address,
    country: form.values.country,
  });

  async function handleAutoCompleteSubmit(item: AutocompleteItem): Promise<void> {
    const { city, state, address, zip } = await getPlaceDetail(item.data.place_id);

    form.setValues((currentState) => ({
      ...currentState,
      address,
      city,
      state,
      zip,
      country: form.values.country || Countries.US,
    }));
  }

  function handleSubmit(): void {
    reportEvent('KYC > Set Address');
    onClick();
  }

  const handleKeyDown = createEnterKeyHandler(isButtonDisabled, handleSubmit);

  const header = getHeaderStrings(mode, t);

  return {
    formFieldChange,
    stateFieldChange,
    countrySelectData,
    isButtonDisabled,
    dataForAutocomplete,
    handleAutoCompleteSubmit,
    handleSubmit,
    handleKeyDown,
    header,
  };
}
